// utils/externalNotificationService.js
import { storageService } from './storageService';
import { apiService } from './apiService';
import pushNotificationService from './pushNotificationService';
import { userIdentificationService } from './userIdentificationService';

/**
 * External Notification Service
 * Handles all types of external notifications for the PWA
 */
class ExternalNotificationService {
  constructor() {
    this.isInitialized = false;
    this.eventListeners = new Map();
    this.notificationQueue = [];
    this.retryAttempts = new Map();
    this.maxRetries = 3;
    
    // Notification channels
    this.channels = {
      PUSH: 'push',
      WEBSOCKET: 'websocket', 
      EMAIL: 'email',
      SMS: 'sms',
      WEBHOOK: 'webhook'
    };

    // Notification types
    this.types = {
      EVENT_REMINDER: 'event_reminder',
      EXHIBITOR_UPDATE: 'exhibitor_update', 
      SCHEDULE_CHANGE: 'schedule_change',
      GENERAL_ANNOUNCEMENT: 'general_announcement',
      REGISTRATION_CONFIRMATION: 'registration_confirmation',
      QR_SCAN_SUCCESS: 'qr_scan_success',
      SYSTEM_MAINTENANCE: 'system_maintenance',
      EXHIBITION_START: 'exhibition_start',
      EXHIBITION_END: 'exhibition_end',
      FAVORITE_EXHIBITOR_UPDATE: 'favorite_exhibitor_update'
    };

    this.websocket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * Initialize the external notification service
   */
  async initialize(config = {}) {
    if (this.isInitialized) {
      console.log('🔔 External notification service already initialized');
      return;
    }

    try {
      console.log('🚀 Initializing external notification service...');

      // Get user identification
      const userIdentification = userIdentificationService.getUserIdentification();
      this.userId = userIdentification.persistentUserId;
      this.sessionId = userIdentification.sessionId;

      // Initialize push notifications
      if (pushNotificationService.isSupported()) {
        await this.initializePushNotifications();
      }

      // Initialize WebSocket connection for real-time notifications
      await this.initializeWebSocket(config.websocketUrl);

      // Set up service worker message listener
      this.setupServiceWorkerListener();

      // Set up online/offline listeners
      this.setupConnectivityListeners();

      // Process any queued notifications
      await this.processNotificationQueue();

      // Set up periodic sync for missed notifications
      this.setupPeriodicSync();

      this.isInitialized = true;
      console.log('✅ External notification service initialized successfully');
      
      // Send initialization event to backend
      await this.sendNotificationEvent('service_initialized', {
        userId: this.userId,
        sessionId: this.sessionId,
        capabilities: this.getCapabilities()
      });

    } catch (error) {
      console.error('❌ Failed to initialize external notification service:', error);
      // Don't throw - allow app to continue functioning
    }
  }

  /**
   * Initialize push notifications
   */
  async initializePushNotifications() {
    try {
      if (Notification.permission === 'granted') {
        console.log('📱 Push notifications already granted, setting up subscription');
        await pushNotificationService.subscribe();
      } else {
        console.log('📱 Push notifications not granted yet');
      }
    } catch (error) {
      console.error('❌ Failed to initialize push notifications:', error);
    }
  }

  /**
   * Initialize WebSocket connection
   */
  async initializeWebSocket(websocketUrl) {
    if (!websocketUrl) {
      websocketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'wss://api.helexpo.gr/notifications';
    }

    try {
      this.websocket = new WebSocket(websocketUrl);
      
      this.websocket.onopen = () => {
        console.log('🔌 WebSocket connected');
        this.reconnectAttempts = 0;
        
        // Send authentication
        this.websocket.send(JSON.stringify({
          type: 'auth',
          userId: this.userId,
          sessionId: this.sessionId,
          token: process.env.REACT_APP_USETOKEN
        }));
      };

      this.websocket.onmessage = (event) => {
        this.handleWebSocketMessage(event);
      };

      this.websocket.onclose = () => {
        console.log('🔌 WebSocket disconnected');
        this.scheduleReconnect();
      };

      this.websocket.onerror = (error) => {
        console.error('🔌 WebSocket error:', error);
      };

    } catch (error) {
      console.error('❌ Failed to initialize WebSocket:', error);
    }
  }

  /**
   * Handle WebSocket messages
   */
  handleWebSocketMessage(event) {
    try {
      const data = JSON.parse(event.data);
      console.log('🔔 Received WebSocket notification:', data);

      switch (data.type) {
        case 'notification':
          this.processIncomingNotification(data.payload, this.channels.WEBSOCKET);
          break;
        case 'broadcast':
          this.processBroadcastMessage(data.payload);
          break;
        case 'ping':
          // Respond to keep-alive ping
          this.websocket.send(JSON.stringify({ type: 'pong' }));
          break;
        default:
          console.log('🔔 Unknown WebSocket message type:', data.type);
      }
    } catch (error) {
      console.error('❌ Error processing WebSocket message:', error);
    }
  }

  /**
   * Schedule WebSocket reconnection
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('🔌 Max reconnection attempts reached');
      return;
    }

    const delay = Math.pow(2, this.reconnectAttempts) * 1000; // Exponential backoff
    this.reconnectAttempts++;

    console.log(`🔌 Scheduling WebSocket reconnection in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    setTimeout(() => {
      this.initializeWebSocket();
    }, delay);
  }

  /**
   * Set up service worker message listener
   */
  setupServiceWorkerListener() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'push-notification') {
          this.handleServiceWorkerNotification(event.data.payload);
        }
      });
    }
  }

  /**
   * Set up connectivity listeners
   */
  setupConnectivityListeners() {
    window.addEventListener('online', () => {
      console.log('🌐 Back online, reconnecting services...');
      this.handleConnectivityChange(true);
    });

    window.addEventListener('offline', () => {
      console.log('🌐 Gone offline, queuing notifications...');
      this.handleConnectivityChange(false);
    });
  }

  /**
   * Handle connectivity changes
   */
  async handleConnectivityChange(isOnline) {
    if (isOnline) {
      // Reconnect WebSocket if needed
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        await this.initializeWebSocket();
      }

      // Process queued notifications
      await this.processNotificationQueue();

      // Sync missed notifications
      await this.syncMissedNotifications();
    } else {
      // Store notifications to queue when offline
      this.isOffline = true;
    }
  }

  /**
   * Process incoming notification from any channel
   */
  async processIncomingNotification(notification, channel) {
    try {
      console.log(`🔔 Processing ${channel} notification:`, notification);

      // Validate notification
      if (!this.validateNotification(notification)) {
        console.error('❌ Invalid notification format:', notification);
        return;
      }

      // Check user preferences
      const preferences = storageService.getItem('notificationPreferences', {});
      if (!this.shouldShowNotification(notification, preferences)) {
        console.log('🔕 Notification filtered by user preferences');
        return;
      }

      // Store notification
      await this.storeNotification(notification);

      // Show notification based on channel and type
      await this.displayNotification(notification, channel);

      // Trigger event listeners
      this.triggerEventListeners('notification', { notification, channel });

      // Send acknowledgment
      await this.acknowledgeNotification(notification.id, channel);

    } catch (error) {
      console.error('❌ Error processing notification:', error);
      this.queueNotificationForRetry(notification, channel);
    }
  }

  /**
   * Display notification to user
   */
  async displayNotification(notification, channel) {
    const { title, body, icon, badge, actions, data } = notification;

    // Show browser notification if supported and permitted
    if (Notification.permission === 'granted') {
      const notif = new Notification(title, {
        body,
        icon: icon || '/icon-192x192.png',
        badge: badge || '/badge-72x72.png',
        data,
        actions: actions || [],
        tag: notification.type,
        requireInteraction: notification.priority === 'high',
        silent: notification.priority === 'low'
      });

      notif.onclick = () => {
        this.handleNotificationClick(notification);
        notif.close();
      };

      // Auto-close after delay unless high priority
      if (notification.priority !== 'high') {
        setTimeout(() => {
          notif.close();
        }, notification.autoClose || 5000);
      }
    }

    // Show in-app notification for immediate visibility
    this.showInAppNotification(notification);
  }

  /**
   * Show in-app notification
   */
  showInAppNotification(notification) {
    // Create toast notification element
    const toast = document.createElement('div');
    toast.className = 'external-notification-toast';
    toast.innerHTML = `
      <div class="notification-content">
        <div class="notification-header">
          <strong>${notification.title}</strong>
          <button class="notification-close">&times;</button>
        </div>
        <div class="notification-body">${notification.body}</div>
        ${notification.actions ? `
          <div class="notification-actions">
            ${notification.actions.map(action => 
              `<button class="notification-action" data-action="${action.action}">${action.title}</button>`
            ).join('')}
          </div>
        ` : ''}
      </div>
    `;

    // Add styles
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      max-width: 350px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      animation: slideIn 0.3s ease-out;
      border-left: 4px solid ${this.getNotificationColor(notification.type)};
    `;

    // Add click handlers
    toast.querySelector('.notification-close').onclick = () => {
      this.removeInAppNotification(toast);
    };

    toast.querySelectorAll('.notification-action').forEach(button => {
      button.onclick = () => {
        this.handleNotificationAction(notification, button.dataset.action);
        this.removeInAppNotification(toast);
      };
    });

    // Click on notification body
    toast.querySelector('.notification-content').onclick = (e) => {
      if (!e.target.matches('button')) {
        this.handleNotificationClick(notification);
        this.removeInAppNotification(toast);
      }
    };

    document.body.appendChild(toast);

    // Auto-remove after delay
    setTimeout(() => {
      this.removeInAppNotification(toast);
    }, notification.autoClose || 8000);
  }

  /**
   * Remove in-app notification
   */
  removeInAppNotification(toast) {
    if (toast && toast.parentNode) {
      toast.style.animation = 'slideOut 0.3s ease-in forwards';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }
  }

  /**
   * Get notification color based on type
   */
  getNotificationColor(type) {
    const colors = {
      [this.types.EVENT_REMINDER]: '#0ea5e9',
      [this.types.EXHIBITOR_UPDATE]: '#10b981', 
      [this.types.SCHEDULE_CHANGE]: '#f59e0b',
      [this.types.GENERAL_ANNOUNCEMENT]: '#6366f1',
      [this.types.SYSTEM_MAINTENANCE]: '#ef4444',
      default: '#6b7280'
    };
    return colors[type] || colors.default;
  }

  /**
   * Handle notification click
   */
  handleNotificationClick(notification) {
    console.log('🔔 Notification clicked:', notification);

    // Navigate based on notification type and data
    if (notification.data?.url) {
      if (notification.data.url.startsWith('/')) {
        // Internal navigation
        window.location.href = notification.data.url;
      } else {
        // External URL
        window.open(notification.data.url, '_blank');
      }
    } else {
      // Default navigation based on type
      this.handleDefaultNavigation(notification);
    }

    // Track click event
    this.trackNotificationEvent('clicked', notification);
  }

  /**
   * Handle notification action
   */
  handleNotificationAction(notification, action) {
    console.log('🔔 Notification action:', action, notification);

    switch (action) {
      case 'view':
        this.handleNotificationClick(notification);
        break;
      case 'dismiss':
        // Do nothing, notification will be removed
        break;
      case 'snooze':
        this.scheduleNotificationSnooze(notification);
        break;
      default:
        console.log('Unknown notification action:', action);
    }

    // Track action event
    this.trackNotificationEvent('action', notification, { action });
  }

  /**
   * Schedule notification for later (snooze)
   */
  scheduleNotificationSnooze(notification, delay = 10 * 60 * 1000) { // 10 minutes default
    setTimeout(() => {
      this.processIncomingNotification({
        ...notification,
        id: `${notification.id}-snooze-${Date.now()}`,
        title: `Reminder: ${notification.title}`
      }, 'scheduled');
    }, delay);
  }

  /**
   * Send notification to external service
   */
  async sendExternalNotification(type, data, channels = ['push']) {
    try {
      console.log('📤 Sending external notification:', { type, data, channels });

      const notification = {
        id: `ext-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type,
        title: data.title,
        body: data.body,
        data: data.data || {},
        channels,
        userId: this.userId,
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        priority: data.priority || 'normal'
      };

      // Send via API to backend
      const response = await apiService.client.post('notifications/send', notification);
      
      console.log('📤 External notification sent successfully:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Failed to send external notification:', error);
      
      // Queue for retry if offline
      this.queueNotificationForRetry({ type, data, channels }, 'api');
      throw error;
    }
  }

  /**
   * Subscribe to exhibition notifications
   */
  async subscribeToExhibition(exhibitionId) {
    try {
      console.log('🔔 Subscribing to exhibition notifications:', exhibitionId);

      const subscription = {
        userId: this.userId,
        sessionId: this.sessionId,
        exhibitionId,
        channels: this.getEnabledChannels(),
        preferences: storageService.getItem('notificationPreferences', {}),
        timestamp: new Date().toISOString()
      };

      const response = await apiService.client.post('notifications/subscribe', subscription);
      
      console.log('✅ Subscribed to exhibition notifications');
      return response.data;

    } catch (error) {
      console.error('❌ Failed to subscribe to exhibition notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from exhibition notifications
   */
  async unsubscribeFromExhibition(exhibitionId) {
    try {
      console.log('🔕 Unsubscribing from exhibition notifications:', exhibitionId);

      const response = await apiService.client.post('notifications/unsubscribe', {
        userId: this.userId,
        exhibitionId,
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ Unsubscribed from exhibition notifications');
      return response.data;

    } catch (error) {
      console.error('❌ Failed to unsubscribe from exhibition notifications:', error);
      throw error;
    }
  }

  /**
   * Schedule event reminders
   */
  async scheduleEventReminder(event, reminderTime = 30) { // 30 minutes before
    try {
      const eventTime = new Date(event.date + ' ' + event.TimeFrom);
      const reminderDateTime = new Date(eventTime.getTime() - (reminderTime * 60 * 1000));
      
      if (reminderDateTime > new Date()) {
        console.log(`⏰ Scheduling event reminder for: ${reminderDateTime}`);

        await this.sendExternalNotification(this.types.EVENT_REMINDER, {
          title: 'Event Reminder',
          body: `${event.titleEng || event.titleGr} starts in ${reminderTime} minutes`,
          data: {
            eventId: event.EventID,
            url: '/events'
          },
          priority: 'high',
          scheduleFor: reminderDateTime.toISOString()
        });
      }
    } catch (error) {
      console.error('❌ Failed to schedule event reminder:', error);
    }
  }

  /**
   * Get enabled notification channels
   */
  getEnabledChannels() {
    const channels = [];
    
    if (pushNotificationService.isSupported() && Notification.permission === 'granted') {
      channels.push(this.channels.PUSH);
    }
    
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      channels.push(this.channels.WEBSOCKET);
    }
    
    return channels;
  }

  /**
   * Get service capabilities
   */
  getCapabilities() {
    return {
      pushNotifications: pushNotificationService.isSupported() && Notification.permission === 'granted',
      webSocket: !!this.websocket,
      serviceWorker: 'serviceWorker' in navigator,
      geolocation: 'geolocation' in navigator,
      vibration: 'vibrate' in navigator
    };
  }

  /**
   * Validate notification format
   */
  validateNotification(notification) {
    return notification && 
           typeof notification.title === 'string' &&
           typeof notification.body === 'string' &&
           notification.type;
  }

  /**
   * Check if notification should be shown based on preferences
   */
  shouldShowNotification(notification, preferences) {
    // Default to showing notifications if preferences not set
    if (!preferences || Object.keys(preferences).length === 0) {
      return true;
    }

    // Check type-specific preferences
    switch (notification.type) {
      case this.types.EVENT_REMINDER:
        return preferences.eventReminders !== false;
      case this.types.EXHIBITOR_UPDATE:
        return preferences.exhibitorUpdates !== false;
      case this.types.SCHEDULE_CHANGE:
        return preferences.scheduleChanges !== false;
      case this.types.GENERAL_ANNOUNCEMENT:
        return preferences.generalAnnouncements !== false;
      default:
        return true;
    }
  }

  /**
   * Store notification locally
   */
  async storeNotification(notification) {
    try {
      const stored = storageService.getItem('storedNotifications', []);
      stored.push({
        ...notification,
        receivedAt: new Date().toISOString(),
        read: false
      });
      
      // Keep only last 100 notifications
      const trimmed = stored.slice(-100);
      storageService.setItem('storedNotifications', trimmed);
      
    } catch (error) {
      console.error('❌ Failed to store notification:', error);
    }
  }

  /**
   * Get stored notifications
   */
  getStoredNotifications() {
    return storageService.getItem('storedNotifications', []);
  }

  /**
   * Mark notification as read
   */
  markNotificationAsRead(notificationId) {
    try {
      const stored = this.getStoredNotifications();
      const updated = stored.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      storageService.setItem('storedNotifications', updated);
    } catch (error) {
      console.error('❌ Failed to mark notification as read:', error);
    }
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications() {
    try {
      storageService.setItem('storedNotifications', []);
      console.log('🗑️ All notifications cleared');
    } catch (error) {
      console.error('❌ Failed to clear notifications:', error);
    }
  }

  /**
   * Add event listener
   */
  addEventListener(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  /**
   * Remove event listener
   */
  removeEventListener(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Trigger event listeners
   */
  triggerEventListeners(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('❌ Error in event listener:', error);
        }
      });
    }
  }

  /**
   * Queue notification for retry
   */
  queueNotificationForRetry(notification, channel) {
    const retryKey = `${notification.id || 'unknown'}-${channel}`;
    const attempts = this.retryAttempts.get(retryKey) || 0;
    
    if (attempts < this.maxRetries) {
      this.notificationQueue.push({ notification, channel, attempts: attempts + 1 });
      this.retryAttempts.set(retryKey, attempts + 1);
      console.log(`📥 Queued notification for retry (attempt ${attempts + 1})`);
    } else {
      console.error('❌ Max retry attempts reached for notification:', retryKey);
    }
  }

  /**
   * Process notification queue
   */
  async processNotificationQueue() {
    if (this.notificationQueue.length === 0) return;
    
    console.log(`📥 Processing ${this.notificationQueue.length} queued notifications`);
    
    const queue = [...this.notificationQueue];
    this.notificationQueue = [];
    
    for (const item of queue) {
      try {
        await this.processIncomingNotification(item.notification, item.channel);
      } catch (error) {
        console.error('❌ Failed to process queued notification:', error);
        // Re-queue if not at max attempts
        if (item.attempts < this.maxRetries) {
          this.queueNotificationForRetry(item.notification, item.channel);
        }
      }
    }
  }

  /**
   * Set up periodic sync
   */
  setupPeriodicSync() {
    // Sync every 5 minutes when app is active
    setInterval(() => {
      if (!document.hidden) {
        this.syncMissedNotifications();
      }
    }, 5 * 60 * 1000);
  }

  /**
   * Sync missed notifications from server
   */
  async syncMissedNotifications() {
    try {
      const lastSync = storageService.getItem('lastNotificationSync', 0);
      const response = await apiService.client.get(`notifications/missed`, {
        params: {
          userId: this.userId,
          since: new Date(lastSync).toISOString()
        }
      });

      if (response.data.notifications) {
        console.log(`🔄 Syncing ${response.data.notifications.length} missed notifications`);
        
        for (const notification of response.data.notifications) {
          await this.processIncomingNotification(notification, 'sync');
        }
      }
      
      storageService.setItem('lastNotificationSync', Date.now());
      
    } catch (error) {
      console.error('❌ Failed to sync missed notifications:', error);
    }
  }

  /**
   * Send notification event to backend
   */
  async sendNotificationEvent(event, data) {
    try {
      await apiService.client.post('notifications/events', {
        event,
        data,
        userId: this.userId,
        sessionId: this.sessionId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('❌ Failed to send notification event:', error);
    }
  }

  /**
   * Track notification events
   */
  trackNotificationEvent(event, notification, extra = {}) {
    console.log(`📊 Notification ${event}:`, notification.id, extra);
    
    // Send tracking data to backend
    this.sendNotificationEvent(`notification_${event}`, {
      notificationId: notification.id,
      notificationType: notification.type,
      ...extra
    });
  }

  /**
   * Acknowledge notification receipt
   */
  async acknowledgeNotification(notificationId, channel) {
    try {
      await apiService.client.post('notifications/acknowledge', {
        notificationId,
        channel,
        userId: this.userId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('❌ Failed to acknowledge notification:', error);
    }
  }

  /**
   * Handle default navigation for notifications
   */
  handleDefaultNavigation(notification) {
    switch (notification.type) {
      case this.types.EVENT_REMINDER:
        window.location.href = '/events';
        break;
      case this.types.EXHIBITOR_UPDATE:
        window.location.href = '/exhibitors';
        break;
      case this.types.SCHEDULE_CHANGE:
        window.location.href = '/events';
        break;
      case this.types.GENERAL_ANNOUNCEMENT:
        window.location.href = '/info';
        break;
      default:
        window.location.href = '/';
    }
  }

  /**
   * Destroy the service
   */
  destroy() {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    
    this.eventListeners.clear();
    this.notificationQueue = [];
    this.retryAttempts.clear();
    this.isInitialized = false;
    
    console.log('🔔 External notification service destroyed');
  }
}

// Create singleton instance
export const externalNotificationService = new ExternalNotificationService();
export default externalNotificationService;

// Add CSS styles for notifications
const notificationStyles = `
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

.external-notification-toast {
  font-family: system-ui, -apple-system, sans-serif;
}

.notification-content {
  padding: 16px;
}

.notification-header {
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 8px;
}

.notification-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
  color: #666;
}

.notification-close:hover {
  color: #000;
}

.notification-body {
  color: #666;
  line-height: 1.4;
  margin-bottom: 12px;
}

.notification-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.notification-action {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
}

.notification-action:hover {
  background: #f5f5f5;
}

.notification-action:first-child {
  background: #0ea5e9;
  color: white;
  border-color: #0ea5e9;
}

.notification-action:first-child:hover {
  background: #0284c7;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = notificationStyles;
  document.head.appendChild(styleSheet);
}